// ============================================================================
// COMPARE PANEL
//
// Renders two VisualizerCanvas instances side-by-side driven by a single
// shared playback cursor from useCompareRunner.  Also shows a complexity
// comparison table in the right panel via the onRenderComplexity render-prop.
// ============================================================================

import React, { useCallback } from 'react';
import {
  Play, Pause, SkipBack, SkipForward, ChevronFirst, ChevronLast,
  X, Clock, Database, Layers, Loader2,
} from 'lucide-react';
import { VisualizerCanvas } from '../canvas/VisualizerCanvas';
import type { UseCompareRunnerReturn } from '../../../core/hooks/useCompareRunner';
import type { AlgorithmMeta } from '../../../core/registry/algorithmRegistry';

// ── Props ─────────────────────────────────────────────────────────────────────

export interface ComparePanelProps {
  /** The shared runner instance from App.tsx — must be passed down, NOT created internally. */
  runner: UseCompareRunnerReturn;
  /** Called when user clicks "Clear" on a slot — parent can react if needed */
  onClearSlot?: (slot: 'A' | 'B') => void;
}

// ── Speed options ─────────────────────────────────────────────────────────────

const SPEED_OPTIONS = [0.5, 1, 2, 4] as const;

// ── Component ─────────────────────────────────────────────────────────────────

export const ComparePanel: React.FC<ComparePanelProps> = ({ runner, onClearSlot }) => {
  const handleClear = useCallback((slot: 'A' | 'B') => {
    runner.clearSlot(slot);
    onClearSlot?.(slot);
  }, [runner, onClearSlot]);

  const hasAny   = !!runner.slotA.algo || !!runner.slotB.algo;
  const hasBoth  = !!runner.slotA.algo && !!runner.slotB.algo;
  const hasSteps = runner.totalSteps > 0;

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">

      {/* ── Two-up canvas area ─────────────────────────────────────────────── */}
      <div className="flex-1 flex min-h-0 overflow-hidden gap-px compare-canvas-area" style={{ background: 'rgba(255,255,255,0.04)' }}>

        <SlotCanvas
          label="A"
          slot={runner.slotA}
          currentStepIndex={runner.currentStepIndex}
          onClear={() => handleClear('A')}
        />

        {/* Divider */}
        <div className="w-px flex-shrink-0 compare-divider" style={{ background: 'rgba(255,255,255,0.06)' }} />

        <SlotCanvas
          label="B"
          slot={runner.slotB}
          currentStepIndex={runner.currentStepIndex}
          onClear={() => handleClear('B')}
        />
      </div>

      {/* ── Shared playback bar ─────────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2 flex-wrap compare-playback-bar"
        style={{
          borderTop: '1px solid rgba(255,255,255,0.07)',
          background: 'rgba(0,0,0,0.35)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
        }}
      >
        {/* Transport */}
        <div className="flex items-center gap-1">
          <TransportButton onClick={runner.reset} disabled={!hasSteps} title="To start">
            <ChevronFirst className="w-3.5 h-3.5" />
          </TransportButton>
          <TransportButton onClick={runner.stepBackward} disabled={!runner.canStepBackward} title="Step back">
            <SkipBack className="w-3.5 h-3.5" />
          </TransportButton>
          <button
            onClick={runner.isPlaying ? runner.pause : runner.play}
            disabled={!hasSteps}
            title={runner.isPlaying ? 'Pause' : 'Play'}
            className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 disabled:opacity-30 disabled:cursor-not-allowed flex-shrink-0"
            style={{
              background: hasSteps ? 'rgba(59,130,246,0.22)' : 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(59,130,246,0.35)',
              color: '#93c5fd',
            }}
          >
            {runner.isPlaying
              ? <Pause className="w-4 h-4" />
              : <Play className="w-4 h-4 translate-x-px" />}
          </button>
          <TransportButton onClick={runner.stepForward} disabled={!runner.canStepForward} title="Step forward">
            <SkipForward className="w-3.5 h-3.5" />
          </TransportButton>
          <TransportButton
            onClick={() => runner.seekTo(runner.totalSteps - 1)}
            disabled={!hasSteps}
            title="To end"
          >
            <ChevronLast className="w-3.5 h-3.5" />
          </TransportButton>
        </div>

        {/* Scrubber */}
        <div className="flex-1 flex items-center gap-2 min-w-0">
          <input
            type="range"
            min={0}
            max={Math.max(runner.totalSteps - 1, 0)}
            value={runner.currentStepIndex}
            onChange={(e) => runner.seekTo(Number(e.target.value))}
            disabled={!hasSteps}
            className="flex-1 h-1 appearance-none rounded-full outline-none disabled:opacity-30"
            style={{ accentColor: '#3b82f6' }}
            aria-label="Seek playback position"
          />
          <span className="text-[10px] font-mono text-zinc-500 tabular-nums flex-shrink-0">
            {hasSteps ? `${runner.currentStepIndex + 1} / ${runner.totalSteps}` : '— / —'}
          </span>
        </div>

        {/* Speed */}
        <div className="hidden sm:flex items-center gap-1 flex-shrink-0">
          {SPEED_OPTIONS.map((s) => (
            <button
              key={s}
              onClick={() => runner.setSpeed(s)}
              className="px-1.5 py-0.5 rounded text-[10px] font-mono transition-colors duration-100"
              style={{
                background: runner.speed === s ? 'rgba(59,130,246,0.2)' : 'transparent',
                color: runner.speed === s ? '#93c5fd' : '#52525b',
                border: runner.speed === s ? '1px solid rgba(59,130,246,0.3)' : '1px solid transparent',
              }}
            >
              {s}×
            </button>
          ))}
        </div>
      </div>

      {/* ── Complexity comparison ───────────────────────────────────────────── */}
      {hasAny && (
        <div
          className="flex-shrink-0 px-3 sm:px-4 py-2.5 flex items-stretch gap-3 flex-wrap"
          style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}
        >
          <ComplexityCard label="A" algo={runner.slotA.algo} accent="#3b82f6" />
          {hasBoth && (
            <div className="w-px flex-shrink-0" style={{ background: 'rgba(255,255,255,0.06)' }} />
          )}
          <ComplexityCard label="B" algo={runner.slotB.algo} accent="#a78bfa" />
        </div>
      )}
    </div>
  );
};

// ── Slot canvas sub-component ─────────────────────────────────────────────────

interface SlotCanvasProps {
  label: 'A' | 'B';
  slot: UseCompareRunnerReturn['slotA'];
  currentStepIndex: number;
  onClear: () => void;
}

const SlotCanvas: React.FC<SlotCanvasProps> = ({
  label, slot, currentStepIndex, onClear,
}) => {
  const accentColor = label === 'A' ? '#3b82f6' : '#a78bfa';
  const currentStep = slot.steps[currentStepIndex] ?? null;

  return (
    <div className="flex-1 flex flex-col min-w-0 min-h-0 relative" style={{ background: 'transparent' }}>

      {/* Slot header */}
      <div
        className="flex items-center gap-2 px-3 py-1.5 flex-shrink-0"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
      >
        {/* Label badge */}
        <span
          className="w-5 h-5 rounded flex items-center justify-center text-[10px] font-bold flex-shrink-0"
          style={{ background: `${accentColor}22`, color: accentColor, border: `1px solid ${accentColor}44` }}
        >
          {label}
        </span>

        <div className="flex-1 min-w-0">
          {slot.algo ? (
            <p className="text-[11px] font-medium text-zinc-300 truncate leading-none">{slot.algo.name}</p>
          ) : (
            <p className="text-[11px] text-zinc-600">Select an algorithm from the sidebar</p>
          )}
        </div>

        {/* Step count chip */}
        {slot.steps.length > 0 && (
          <span
            className="text-[9px] font-mono px-1.5 py-0.5 rounded"
            style={{ background: 'rgba(255,255,255,0.05)', color: '#52525b' }}
          >
            {slot.steps.length} steps
          </span>
        )}

        {/* Loading spinner */}
        {slot.isLoading && (
          <Loader2 className="w-3 h-3 animate-spin flex-shrink-0" style={{ color: accentColor }} />
        )}

        {/* Clear */}
        {slot.algo && !slot.isLoading && (
          <button
            onClick={onClear}
            className="w-5 h-5 rounded flex items-center justify-center transition-colors flex-shrink-0"
            style={{ color: '#3f3f46' }}
            title="Clear slot"
            onMouseEnter={e => { (e.currentTarget as HTMLButtonElement).style.color = '#ef4444'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#3f3f46'; }}
          >
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {/* Canvas area */}
      <div className="flex-1 overflow-hidden relative min-h-0">
        {slot.isLoading ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
            <div className="relative">
              <div className="w-8 h-8 rounded-full border border-blue-500/15" />
              <div className="absolute inset-0 w-8 h-8 rounded-full border-2 border-transparent border-t-blue-500 animate-spin" />
            </div>
            <p className="text-[10px] text-zinc-600 font-mono">Running…</p>
          </div>
        ) : slot.error ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-4 text-center">
            <p className="text-[11px] text-rose-400 font-mono">{slot.error}</p>
          </div>
        ) : slot.algo && slot.steps.length > 0 && currentStep ? (
          <VisualizerCanvas
            key={`compare-${label}-${slot.algo.id}`}
            currentStep={currentStep}
            algorithmType={slot.algo.category}
            algorithmId={slot.algo.id}
            allSteps={slot.steps}
            stepIndex={currentStepIndex}
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <div
              className="w-12 h-12 rounded-xl flex items-center justify-center"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.06)',
              }}
            >
              <Layers className="w-5 h-5" style={{ color: 'rgba(255,255,255,0.12)' }} />
            </div>
            <p className="text-[11px] text-zinc-600 text-center max-w-[140px] leading-snug">
              {slot.algo ? `Preparing ${slot.algo.name}…` : `Slot ${label}: pick an algorithm`}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// ── Complexity card ───────────────────────────────────────────────────────────

const ComplexityCard: React.FC<{ label: string; algo: AlgorithmMeta | null; accent: string }> = ({
  label, algo, accent,
}) => (
  <div className="flex-1 flex items-center gap-2 min-w-0">
    <span
      className="w-4 h-4 rounded text-[9px] font-bold flex items-center justify-center flex-shrink-0"
      style={{ background: `${accent}22`, color: accent, border: `1px solid ${accent}44` }}
    >
      {label}
    </span>
    {algo ? (
      <div className="flex items-center gap-2 min-w-0 flex-wrap">
        <span className="text-[10px] font-medium text-zinc-400 truncate">{algo.name}</span>
        <span
          className="flex items-center gap-1 px-1.5 py-0.5 rounded"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <Clock className="w-2.5 h-2.5 text-emerald-500" />
          <span className="text-[9px] font-mono text-zinc-400">{algo.complexity.time}</span>
        </span>
        <span
          className="flex items-center gap-1 px-1.5 py-0.5 rounded"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)' }}
        >
          <Database className="w-2.5 h-2.5 text-violet-400" />
          <span className="text-[9px] font-mono text-zinc-400">{algo.complexity.space}</span>
        </span>
      </div>
    ) : (
      <span className="text-[10px] text-zinc-700">—</span>
    )}
  </div>
);

// ── Transport button ──────────────────────────────────────────────────────────

const TransportButton: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
}> = ({ onClick, disabled, title, children }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    className="w-7 h-7 rounded-md flex items-center justify-center transition-colors duration-100 disabled:opacity-30 disabled:cursor-not-allowed"
    style={{ color: '#71717a' }}
    onMouseEnter={e => { if (!disabled) (e.currentTarget as HTMLButtonElement).style.color = '#d4d4d8'; }}
    onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.color = '#71717a'; }}
  >
    {children}
  </button>
);
